import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import Text from '@/components/ui/custom/dataDisplay/Text';
import { useSingleCache } from '@/data/hook/query/v2/useCacheManager';
import useTreePrompt from '@/data/hook/useTreePrompt';
import { promptInstructionsQueryKeys } from '@/data/services/querykeys';
import PromptInstruction, {
  PromptInstructionHistory,
} from '@/models/PromptInstruction';
import useFilterParams from '@/utils/UseFilterParams';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import PromptsHistorySidebar from '../history/PromptsHistorySidebar';
import PromptsVersionComparator from '../history/PromptsVersionComparator';
import { PromptsFiltersParams } from '../PromptsPageContent';
import PromptsForm from './PromptsForm';
import useToggle from '@/data/hook/useToggle';

export type PromptInstructionHistoryWithVersion = PromptInstructionHistory & {
  version: number;
};

export default function PromptsWorkspace() {
  const { t } = useTranslation('translation', {
    keyPrefix: 'prompts',
  });

  const { filterParams } = useFilterParams<PromptsFiltersParams>();
  const promptId = Number(filterParams.get('prompt') ?? NaN);

  const { isOpen: historyOpen, toggle: toggleHistory, close } = useToggle();
  const [selectedVersion, setSelectedVersion] =
    useState<PromptInstructionHistoryWithVersion | null>(null);

  const promptKey = promptInstructionsQueryKeys.get(promptId);
  const { data: prompt, isLoading } = useQuery({
    ...promptKey,
    enabled: !isNaN(promptId),
  });

  const singleCacheManager = useSingleCache(promptKey.queryKey);

  const onUpdateByObserver = useCallback(
    (prompt?: PromptInstruction) => {
      if (!prompt || prompt.id !== promptId) return;
      singleCacheManager.set(prompt);
    },
    [promptId, singleCacheManager],
  );

  const { data: historyData, isLoading: isLoadingHistory } = useQuery({
    ...promptInstructionsQueryKeys.history(promptId, {
      ordering: '-history_date',
    }),
    enabled: !isNaN(promptId) && historyOpen,
  });

  const history = historyData?.results ?? [];

  const handleCloseHistory = () => {
    close();
    setSelectedVersion(null);
  };

  const handleSelectVersion = (
    version: PromptInstructionHistoryWithVersion,
  ) => {
    setSelectedVersion(version);
  };

  const handleRestoreComplete = () => {
    setSelectedVersion(null);
  };

  const { cacheObserver } = useTreePrompt();

  useEffect(() => {
    if (!prompt) return;
    cacheObserver.subscribe({ id: prompt.id, action: onUpdateByObserver });
    return () => cacheObserver.unsubscribe(prompt.id);
  }, [cacheObserver, onUpdateByObserver, prompt]);

  return (
    <div className="flex grow h-full">
      <div
        className={twMerge(
          'relative grow flex flex-col gap-2.5 p-3 border border-primary rounded-xl bg-primary-content',
          historyOpen && 'border-r-transparent rounded-tr-none rounded-br-none',
        )}
      >
        <ConditionalRenderer
          condition={prompt && !isLoading}
          fallback={
            <div className="w-full h-full flex justify-center items-center">
              <Text text={t('workspace.selectPrompt')} />
            </div>
          }
        >
          {prompt && (
            <ConditionalRenderer
              condition={selectedVersion}
              fallback={
                <PromptsForm
                  prompt={prompt}
                  key={JSON.stringify(prompt)}
                  onOpenHistory={toggleHistory}
                />
              }
            >
              {selectedVersion && (
                <PromptsVersionComparator
                  key={`${selectedVersion.historyId}-${selectedVersion.version}`}
                  currentPrompt={prompt}
                  selectedVersion={selectedVersion}
                  onRestoreComplete={handleRestoreComplete}
                />
              )}
            </ConditionalRenderer>
          )}
        </ConditionalRenderer>
      </div>

      {historyOpen && prompt && (
        <PromptsHistorySidebar
          history={history}
          selectedVersion={selectedVersion ?? undefined}
          isLoading={isLoadingHistory}
          onClose={handleCloseHistory}
          onSelectVersion={handleSelectVersion}
        />
      )}
    </div>
  );
}
